import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { getImageUrl } from "../../utils/portfolioUtils";
import ArxivButton from "../homepage/buttons/ArxivButton";
import GithubButton from "../homepage/buttons/GithubButton";
import SlidesButton from "../homepage/buttons/SlidesButton";
import LoadingState from "../common/LoadingState";

const Publications = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedVenue, setSelectedVenue] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/publications/`,
        );
        setPublications(response.data);
      } catch (err) {
        console.error("Error fetching publications:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPublications();
  }, []);

  const venues = useMemo(() => {
    const venueSet = new Set();
    publications.forEach((pub) => {
      if (pub.venue) venueSet.add(pub.venue);
    });
    return Array.from(venueSet).sort();
  }, [publications]);

  const years = useMemo(() => {
    const yearSet = new Set();
    publications.forEach((pub) => {
      if (pub.year) yearSet.add(pub.year);
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [publications]);

  const filteredPublications = useMemo(() => {
    const filtered = publications.filter((pub) => {
      const matchesTitle = searchTitle
        ? pub.title.toLowerCase().includes(searchTitle.toLowerCase())
        : true;
      const matchesVenue = selectedVenue ? pub.venue === selectedVenue : true;
      const matchesYear = selectedYear
        ? pub.year === parseInt(selectedYear)
        : true;
      return matchesTitle && matchesVenue && matchesYear;
    });

    return [...filtered].sort((a, b) =>
      sortAsc ? (a.year ?? 0) - (b.year ?? 0) : (b.year ?? 0) - (a.year ?? 0),
    );
  }, [publications, searchTitle, selectedVenue, selectedYear, sortAsc]);

  const handleClearFilters = () => {
    setSearchTitle("");
    setSelectedVenue("");
    setSelectedYear("");
  };

  const hasActiveFilters = searchTitle || selectedVenue || selectedYear;

  if (loading) return <LoadingState />;

  return (
    <>
      <Helmet>
        <title>Publications — Kaustubh Harapanahalli</title>
        <meta
          name="description"
          content="Research publications by Kaustubh Harapanahalli."
        />
      </Helmet>

      <section className="portfolio-section section-band-1">
        <Container>
          <div className="d-flex align-items-baseline gap-3 mb-4">
            <h2 className="text-start updates-box mb-0">
              <strong>Publications</strong>
            </h2>
            <span className="pub-count-badge">
              {filteredPublications.length}
              {hasActiveFilters && ` of ${publications.length}`}
            </span>
          </div>

          {error ? (
            <p className="text-secondary">
              Couldn&apos;t load publications — please try refreshing the page.
            </p>
          ) : (
            <div className="pub-filter-panel mb-0">
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="pub-filter-label">
                      Search by Title
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Enter title keywords..."
                        value={searchTitle}
                        onChange={(e) => setSearchTitle(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="pub-filter-label">
                      Filter by Venue
                    </Form.Label>
                    <Form.Select
                      value={selectedVenue}
                      onChange={(e) => setSelectedVenue(e.target.value)}
                    >
                      <option value="">All Venues</option>
                      {venues.map((venue) => (
                        <option key={venue} value={venue}>
                          {venue}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="pub-filter-label">
                      Filter by Year
                    </Form.Label>
                    <Form.Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="">All Years</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group>
                    <Form.Label className="pub-filter-label">Sort</Form.Label>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSortAsc((v) => !v)}
                      className="w-100"
                      title={sortAsc ? "Oldest first" : "Newest first"}
                      aria-label={`Sort ${sortAsc ? "newest first" : "oldest first"}`}
                    >
                      {sortAsc ? "↑" : "↓"}
                    </Button>
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters}
                    className="w-100"
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </Container>
      </section>

      <section className="portfolio-section section-band-2">
        <Container>
          {filteredPublications.length === 0 ? (
            <div className="pub-empty-state mb-4">
              <p>No publications found matching your filters.</p>
            </div>
          ) : (
            filteredPublications.map((publication) => (
              <Row key={publication.id} className="mb-4">
                <Col md={4}>
                  {publication.thumbnail && (
                    <img
                      src={getImageUrl(publication.thumbnail)}
                      alt={publication.title}
                      className="img-fluid pub-thumbnail"
                    />
                  )}
                </Col>
                <Col md={8}>
                  <p className="paper-title">
                    <a
                      href={publication.arxiv_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="paper-link"
                    >
                      {publication.title}
                    </a>
                  </p>
                  <p className="authors">{publication.authors}</p>
                  <p className="venue">
                    {publication.venue_link ? (
                      <a
                        href={publication.venue_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="paper-link"
                      >
                        {publication.venue}
                      </a>
                    ) : (
                      publication.venue
                    )}
                    {publication.year && <span> • {publication.year}</span>}
                  </p>
                  <p className="paper-buttons">
                    {publication.arxiv_link && (
                      <ArxivButton link={publication.arxiv_link} />
                    )}
                    {publication.github_link && (
                      <GithubButton link={publication.github_link} />
                    )}
                    {publication.slides_file && (
                      <SlidesButton file={publication.slides_file} />
                    )}
                  </p>
                </Col>
              </Row>
            ))
          )}
        </Container>
      </section>
    </>
  );
};

export default Publications;
